import { type VoidFunctionComponent } from 'react';
import { useDispatch } from 'react-redux';

import { useEditable } from '../../../../../hooks';
import useCorrectDependencyMetadata from '../../../../../hooks/api/correctionsV2/useCorrectDependencyMetadata';
import * as DependenciesV2Store from '../../../../../redux/dependenciesV2';
import { Icon, icons, LabeledTextArea, LabeledTextInput } from '../../../../generic';
import Button, { BUTTON_SIZE, BUTTON_TYPE } from '../../../../generic/Button';
import Flex from '../../../../generic/Flex';
import type { Dependency } from '../../../../../../shared/dependenciesV2';
import type { UpdateProjectCorrectionRequestProject } from '../../../../../../typings/api';

import styles from '../DependencyDrawer.module.less';

type Props = {
  dependency: Dependency;
  toggleEditing: () => void;
};

export const DependencyDetailsEdit: VoidFunctionComponent<Props> = ({ dependency, toggleEditing }) => {
  const dispatch = useDispatch();
  const {
    data: title,
    isDirty: isTitleDirty,
    onUpdate: onUpdateTitle,
    onSave: onSaveTitle,
  } = useEditable(dependency.title);
  const {
    data: description,
    isDirty: isDescriptionDirty,
    onUpdate: onUpdateDescription,
    onSave: onSaveDescription,
  } = useEditable(dependency.description);
  const {
    data: homepage,
    isDirty: isHomepageDirty,
    onUpdate: onUpdateHomepage,
    onSave: onSaveHomepage,
  } = useEditable(dependency.url);
  const { mutate: upsertCorrection, isLoading: isUpsertingCorrection } = useCorrectDependencyMetadata(
    dependency.locator,
  );

  const isDirty = isTitleDirty || isDescriptionDirty || isHomepageDirty;

  const onCancel = () => {
    toggleEditing();
  };

  const onSave = () => {
    const correctionData: Partial<UpdateProjectCorrectionRequestProject> = {
      title,
      description,
      url: homepage,
    };
    upsertCorrection(
      { correctionData, revisionLocator: dependency.locator },
      {
        onSuccess: () => {
          onSaveTitle();
          onSaveDescription();
          onSaveHomepage();
          // Trigger dependencies table reload
          dispatch(DependenciesV2Store.actions.preFetchLoading());
          dispatch(DependenciesV2Store.actions.fetchDependencies());
          toggleEditing();
        },
      },
    );
  };

  return (
    <div>
      <div className={styles.editInput}>
        <LabeledTextInput
          labelVariant="legacy"
          label="Name"
          value={title}
          onChange={(newTitle) => {
            onUpdateTitle(newTitle);
          }}
        />
      </div>
      <div className={styles.editInput}>
        <LabeledTextInput
          labelVariant="legacy"
          label="Homepage"
          value={homepage}
          onChange={(newHomepage) => {
            onUpdateHomepage(newHomepage);
          }}
        />
      </div>
      <div className={styles.editInput}>
        <LabeledTextArea
          labelVariant="legacy"
          label="Description"
          value={description}
          isResizable
          size="large"
          onChange={(newDescription) => {
            onUpdateDescription(newDescription);
          }}
        />
      </div>
      <hr />
      <div className={styles.footer}>
        <Flex gap="8px">
          <Button
            type={BUTTON_TYPE.SECONDARY}
            size={BUTTON_SIZE.MEDIUM}
            onClick={onCancel}
            isDisabled={isUpsertingCorrection}
          >
            Cancel
          </Button>
          <Button
            type={BUTTON_TYPE.PRIMARY}
            size={BUTTON_SIZE.MEDIUM}
            onClick={onSave}
            isDisabled={!isDirty || isUpsertingCorrection}
          >
            {isUpsertingCorrection ? <Icon icon={icons.circleO} hasSpin /> : 'Save'}
          </Button>
        </Flex>
      </div>
    </div>
  );
};
